/**
 * Centralized route utilities for language-prefixed URL paths.
 */

// Valid language slugs in the URL
const LANG_SLUGS = ['en', 'ja', 'zh-tw'];

// Map URL slug to internal lang code
const SLUG_TO_LANG = {
  'en': 'en',
  'ja': 'ja',
  'zh-tw': 'zh'
};

// Map internal lang code to URL slug
const LANG_TO_SLUG = {
  'en': 'en',
  'ja': 'ja',
  'zh': 'zh-tw'
};

/**
 * Convert a URL language slug to internal language code.
 * @param {string} slug - e.g. 'zh-tw', 'en', 'ja'
 * @returns {string} Internal lang code: 'zh', 'en', or 'ja'
 */
export function langFromSlug(slug) {
  return SLUG_TO_LANG[slug] || 'en';
}

/**
 * Convert internal language code to URL slug.
 * @param {string} lang - Internal lang code: 'zh', 'en', or 'ja'
 * @returns {string} URL slug: 'zh-tw', 'en', or 'ja'
 */
export function slugFromLang(lang) {
  return LANG_TO_SLUG[lang] || 'en';
}

/**
 * Build a path with language prefix.
 * @param {string} lang - Internal lang code ('zh', 'en', 'ja')
 * @param {string} route - Route path (e.g. '/about/about_us', '/tech/collie')
 * @returns {string} Full path like '/en/about/about_us'
 */
export function buildPath(lang, route) {
  const slug = slugFromLang(lang);
  const cleanRoute = route.startsWith('/') ? route : '/' + route;
  return '/' + slug + cleanRoute;
}

/**
 * Check if a slug is a valid language slug.
 * @param {string} slug
 * @returns {boolean}
 */
export function isValidLangSlug(slug) {
  return LANG_SLUGS.includes(slug);
}

/**
 * Detect user's preferred language from cookie, then browser settings.
 * @returns {string} URL slug for detected language
 */
export function detectLangSlug() {
  // Check cookie first
  try {
    const cookies = decodeURIComponent(document.cookie).split(';');
    for (let c of cookies) {
      c = c.trim();
      if (c.startsWith('lang=')) {
        const val = c.substring(5);
        if (LANG_TO_SLUG[val]) return LANG_TO_SLUG[val];
      }
    }
  } catch (e) { /* ignore */ }

  // Check user's region via timezone
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz === 'Asia/Taipei') return 'zh-tw';
    if (tz === 'Asia/Tokyo') return 'ja';
  } catch (e) { /* ignore */ }

  return 'en';
}

/**
 * Map old URL paths to new paths.
 * @param {string} oldPath - e.g. '/MainPage', '/GeneralPage'
 * @param {string} query - e.g. 'page=about_us'
 * @returns {string|null} New route segment or null if no mapping
 */
export function mapOldRoute(oldPath, query) {
  const params = new URLSearchParams(query);
  const page = params.get('page');

  const oldRoutes = {
    '/MainPage': '/',
    '/': '/',
    '/SolutionPage': '/solutions',
    '/ContactUsPage': '/contact',
    '/StoryPage': '/stories',
    '/BlogPage': '/blog',
  };

  if (oldRoutes[oldPath]) return oldRoutes[oldPath];

  if (oldPath === '/GeneralPage' && page) {
    return '/about/' + page;
  }
  if (oldPath === '/TechPage' && page) {
    return '/tech/' + page;
  }

  return null;
}
